import React from 'react';
import PropTypes from 'prop-types';
//Componentes Material
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
//Icones
import AddIcon from '@material-ui/icons/Add';
//Componentes
import QuestaoEscolhaOpcao, { TIPO_OPCAO_NUMERICO, TIPO_OPCAO_TEXTO } from './QuestaoEscolhaOpcao';
//Modelos
import QuestionarioQuestao, { TIPO_ESCOLHA } from '../../models/QuestionarioQuestao';
import QuestionarioOpcaoResposta from '../../models/QuestionarioOpcaoResposta';
import ErroQuestionarioQuestao from '../../models/ErroQuestionarioQuestao';

/**
 * Componente para renderizar e manipular as opções das repostas que permitem a seleção
 */
class QuestaoEscolha extends React.Component {
    constructor(props) {
        super(props);
        //Bindings
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        this.handleOpcaoChange = this.handleOpcaoChange.bind(this);
        this.handleAdicionarOpcao = this.handleAdicionarOpcao.bind(this);
        this.handleOpcaoDelete = this.handleOpcaoDelete.bind(this);
    }

    /**
     * Trata a alteração de valor do campo checkbox
     * @param {*} event Objeto contendo os dados do evento
     */
    handleCheckboxChange(event) {
        //Faz uma cópia da questão
        const questao = QuestionarioQuestao.clone(this.props.questao);
        //Atualiza o valor da propriedade
        questao.opcaoOutros = event.target.checked;
        //Dispara o evento de alteração
        this.dispararEventoChange(questao);
    }

    /**
     * Trata quando uma opção de resposta é alterada
     * @param {QuestionarioOpcaoResposta} opcao Objeto contendo os dados atualizados da opção de resposta
     */
    handleOpcaoChange(opcao) {
        //Faz uma cópia da questão
        const questao = QuestionarioQuestao.clone(this.props.questao);
        //Identifica o índice da resposta alterada
        const indice = questao.opcoes.findIndex(r => r.ordem === opcao.ordem);
        //Atualiza o valor na lista
        questao.opcoes[indice] = opcao;
        //Dispara o evento de alteração
        this.dispararEventoChange(questao);
    }

    /**
     * Trata quando a opção de exclusão da opção de resposta é acionada
     * @param {QuestionarioOpcaoResposta} opcao Objeto contendo os dados atualizados da opção de resposta
     */
    handleOpcaoDelete(opcao) {
        //Somente dispara quando não for somente leitura
        if (!this.props.readOnly) {
            //Faz uma cópia da questão
            const questao = QuestionarioQuestao.clone(this.props.questao);
            //Retorna uma lista das opções de resposta, removendo a que foi excluída
            questao.opcoes = questao.opcoes.filter(r => r.ordem !== opcao.ordem);
            //Dispara o evento de alteração
            this.dispararEventoChange(questao);
        }
    }

    /**
     * Trata o click no botão para adicionar novas opções de resposta
     */
    handleAdicionarOpcao() {
        //Faz uma cópia da questão
        const questao = QuestionarioQuestao.clone(this.props.questao);
        //Verifica se a propriedade `opcoes` é um array
        if (!questao.opcoes || !Array.isArray(questao.opcoes))
            questao.opcoes = [];

        //Identifica o último ID inserido
        let ultimaOrdem = 0;
        if (questao.opcoes.length > 0)
            ultimaOrdem = Math.max(...questao.opcoes.map(r => r.ordem));

        //Insere uma nova opção de resposta, incrementando o ID
        questao.opcoes.push(new QuestionarioOpcaoResposta(
            null,
            '',
            '',
            null,
            ultimaOrdem + 1,
            ''
        ));

        //Dispara o evento de alteração
        this.dispararEventoChange(questao);
    }

    /**
     * Dispara o evento de alteração com os dados atualizados
     * @param {QuestionarioQuestao} questao Objeto com os dados atualizados da questão
     */
    dispararEventoChange(questao) {
        //Verifica se o evento está definido e não é somente leitura
        if (!this.props.readOnly && this.props.onChange)
            this.props.onChange(questao);
    }

    /**
     * Renderiza os componentes de opções de resposta de acordo com a lista existente
     * @param {QuestionarioQuestao} questao Questão com as opções de resposta
     * @param {ErroQuestionarioQuestao} erros Mensagens de erro da questão
     */
    renderOpcoesResposta(questao, erros) {
        const { opcoes } = questao;

        //Identificando o tipo da opção
        let tipoOpcao = TIPO_OPCAO_TEXTO;
        if (questao.tipo === TIPO_ESCOLHA && questao.exibeGrafico)
            tipoOpcao = TIPO_OPCAO_NUMERICO;
        
        //Verifica se a lista está definida e se é um array
        if (opcoes && Array.isArray(opcoes)) {
            //Retorna um componente para cada opção de resposta presente
            return opcoes.map((opcao, indice) => 
                <QuestaoEscolhaOpcao 
                    key={opcao.ordem} 
                    opcao={opcao} 
                    erros={erros.opcoes[indice]}
                    focoAutomatico={this.props.focoAutomatico}
                    readOnly={this.props.readOnly}
                    tipoOpcao={tipoOpcao}
                    onChange={this.handleOpcaoChange} 
                    onDelete={this.handleOpcaoDelete}
                />
            );
        }
    }

    /**
     * Renderiza a mensagem de erro referete à lista de opções de resposta
     * @param {ErroQuestionarioQuestao} erros Mensagens de erro da questão
     */
    renderizarMesagemErro(erros) {
        if (erros.erroOpcoes) {
            return (
                <Grid item xs >
                    <FormHelperText id="questaoEscolha-helper" error={!!erros.erroOpcoes}>{erros.erroOpcoes}</FormHelperText>
                </Grid>
            );
        }
    }

    shouldComponentUpdate(nextProps) {
        return !ErroQuestionarioQuestao.compare(nextProps.erros, this.props.erros)
            || !QuestionarioQuestao.compare(nextProps.questao, this.props.questao);
    }

    render() {
        const { questao, erros, readOnly } = this.props;

        return (
            <React.Fragment>
                <Grid item xs={12} sm="auto">
                    <FormControl fullWidth variant="standard" error={!!erros.opcaoOutros}>

                        <FormControlLabel
                            id="outros-label"
                            control={
                                <Checkbox
                                    checked={questao.opcaoOutros}
                                    onChange={this.handleCheckboxChange}
                                    name="opcaoOutros"
                                    color="primary"
                                />
                            }
                            label="Possui opção 'Outro'"
                        />
                        <FormHelperText id="opcaoOutros-helper" >{erros.opcaoOutros}</FormHelperText>
                    </FormControl>
                    { this.renderizarMesagemErro(erros) }
                </Grid>
                { this.renderOpcoesResposta(questao, erros) }
                {
                    !readOnly &&
                        <Grid item xs={12} >
                            <Button 
                                id="adicionar-opcao"
                                startIcon={<AddIcon />}
                                fullWidth 
                                color="primary"
                                onClick={this.handleAdicionarOpcao}
                            >
                            Adicionar opção de resposta
                            </Button>
                        </Grid>
                }
            </React.Fragment>
        );
    }
}

QuestaoEscolha.propTypes = {
    /** Dados da questão */
    questao: PropTypes.instanceOf(QuestionarioQuestao).isRequired,
    /** Mensagens de erro da questão, referente as opções de resposta */
    erros: PropTypes.instanceOf(ErroQuestionarioQuestao),
    /** Indica se o componente terá o foco ao ser criado */
    focoAutomatico: PropTypes.bool,
    /** Identifica se o componente é somente leitura e não permite alterações */
    readOnly: PropTypes.bool,
    /** Evento disparado quando há alteração nas opções da questão */
    onChange: PropTypes.func
};

QuestaoEscolha.defaultProps = {
    erros: new ErroQuestionarioQuestao(),
    readOnly: false
};

export default QuestaoEscolha;